/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { QuestionInputStep } from 'src/languageStudio/appShell/prebuiltDemos/questionAnswersDemo/QuestionInputStep';
import { VisualizedQuestionAnswersResult } from 'src/languageStudio/appShell/prebuiltDemos/questionAnswersDemo/VisualizedQuestionAnswersResult';
import { questionsAnsweringDemoSamples, QuestionsAnswersDemoSample } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import { qnaDemoSupportedRegions } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import {
    questionAnswersApiReferenceLink,
    questionAnswersDocLink,
    questionAnswersSamplesLink,
    questionAnswersSdkLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { Language, questionsAnsweringDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useQuestionsAnswersGenerator } from 'src/languageStudio/core/hooks/useQuestionsAnswersGenerator';
import { QuestionAnsweringResult } from 'src/languageStudio/core/services/questionsAnswering/questionsAnsweringSwagger';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.questionAnswersDemoPage.pageHeader', defaultMessage: 'Answer questions' },
    description: {
        id: 'languageStudio.questionAnswersDemoPage.description',
        defaultMessage: `This prebuilt capability attempts to extract the answer to the
          given question from the passage of text provided. Extract questions and answers
          from your semi-structured content, including FAQs, manuals, and documents.`
    },
    emptyState: {
        id: 'languageStudio.questionAnswersDemoPage.emptyState',
        defaultMessage: `Your answer questions results will appear here once you enter
          or upload some text, enter a question and press Run. The file format needs to be .txt`
    },
    signedOutEmptyState: {
        id: 'languageStudio.questionAnswersDemoPage.signedOutEmptyState',
        defaultMessage: `Your answer questions results will appear here once you press Run.`
    }
});

/**
 * Displays the Answer Questions demo page.
 */
export const QuestionAnswersDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { answer, tracker, detectedLanguages, resetResults, generateAnswer, getCurlCommand } = useQuestionsAnswersGenerator();

    const [document, setDocument] = React.useState('');
    const [question, setQuestion] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const handleDocumentChange = (newDocument: string) => {
        if (answer) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleSampleClick = (sample: QuestionsAnswersDemoSample) => {
        handleDocumentChange(sample?.document ?? '');
        setQuestion(sample?.question ?? '');
        setLanguage(sample?.language ?? 'en');
        setSelectedSampleTitle(sample?.title);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Question answering');
        }

        generateAnswer({
            document,
            question,
            language,
            sampleTitle: selectedSampleTitle,
            autoDetectLanguage: languageAutoDetection
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Question answering"
                supportedPlatforms={['cloud']}
                getSdkLink={questionAnswersSdkLink}
                learnMoreLink={questionAnswersDocLink}
                demoTitle={localize(messages.pageHeader)}
                documentationLink={questionAnswersDocLink}
                gitHubSamplesLink={questionAnswersSamplesLink}
                demoVersion={LuisConstants.QnaMaker.ApiVersion}
                demoDescription={localize(messages.description)}
            />

            <DocumentInputStep
                document={document}
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                featureName="Question answering"
                onSampleClick={handleSampleClick}
                acceptedResourceType="TextAnalytics"
                onDocumentChange={handleDocumentChange}
                onInputClearClick={() => setQuestion('')}
                selectedSampleTitle={selectedSampleTitle}
                supportedRegions={qnaDemoSupportedRegions}
                samplesList={questionsAnsweringDemoSamples}
                supportedRegionLink={questionAnswersApiReferenceLink}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                languagesList={questionsAnsweringDemoSupportedLanguages}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
            />

            <QuestionInputStep question={question} runQuestion={handleRunDemo} onQuestionChange={setQuestion} />

            <ResultsStep<QuestionAnsweringResult>
                results={answer}
                isLoading={tracker.isRunning}
                featureName="Question answering"
                emptyStateMessage={localize(isSignedIn ? messages.emptyState : messages.signedOutEmptyState)}>
                <VisualizedQuestionAnswersResult
                    answers={answer}
                    document={document}
                    detectedLanguage={detectedLanguages}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                isResultsEmpty={!answer}
                sdkLink={questionAnswersSdkLink}
                featureName="Question answering"
                documentationLink={questionAnswersDocLink}
                sampleCodeLink={questionAnswersSamplesLink}
                demosToDisplay={['ner', 'pii', 'taForHealth']}
                curlCommand={getCurlCommand({ language, text: document, languageAutoDetection })}
            />
        </Stack>
    );
});
